import React, { useEffect, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const Hero = ({ websiteData }) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="relative h-[60vh] min-h-[320px] md:h-[60vh] md:min-h-[400px] flex flex-col items-center pt-8 overflow-y-auto overflow-hidden">
      {/* Background Image with Parallax Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src="https://www.thephytgym.com/cdn/shop/files/IMG_8920_1944x.jpg?v=1736389693"
          alt="Coach HT"
          className="w-full h-[130%] object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
      </div>
      {/* Always-on Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/80 via-purple-800/70 to-purple-900/90 z-10 pointer-events-none"></div>

      {/* Floating particles animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-20 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${10 + Math.random() * 20}s`
            }}
          ></div>
        ))}
      </div>

      {/* Content + Scroll Indicator as a flex column */}
      <div className="relative h-full flex flex-col items-center justify-center z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <div className="space-y-6 sm:space-y-8 transform transition-all duration-1000">
            <div className="space-y-4 sm:space-y-6">
              <h1 className="text-2xl xs:text-3xl sm:text-5xl md:text-8xl font-light tracking-tight text-white mb-2 sm:mb-6 animate-fade-in-up leading-tight">
                {websiteData.name}
              </h1>
              <h2 className="text-base xs:text-lg sm:text-2xl md:text-4xl text-purple-100 mb-2 sm:mb-8 font-medium animate-fade-in-up animation-delay-300 leading-tight">
                {websiteData.title}
              </h2>
              <p className="text-xs xs:text-sm sm:text-base md:text-xl text-purple-100 mb-4 sm:mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-up animation-delay-600">
                {websiteData.bio}
              </p>
            </div>
            {/* Enhanced CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-6 animate-fade-in-up animation-delay-900">
              <a 
                href={websiteData.calendarBookingPage}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative px-4 sm:px-10 py-3 sm:py-5 bg-white text-purple-900 font-bold rounded-2xl inline-flex items-center justify-center hover:bg-purple-50 transition-all duration-300 transform hover:scale-105 shadow-2xl overflow-hidden text-sm sm:text-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                <Calendar className="w-4 h-4 sm:w-6 sm:h-6 mr-2 sm:mr-3 relative z-10 group-hover:text-white transition-colors duration-300" />
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">Book a Consultation</span>
              </a>
              <a 
                href="#services"
                className="group px-4 sm:px-10 py-3 sm:py-5 bg-transparent border-3 border-white text-white font-bold rounded-2xl inline-flex items-center justify-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm text-sm sm:text-lg"
              >
                View Programs
                <ChevronDown className="w-4 h-4 sm:w-6 sm:h-6 ml-2 sm:ml-3 group-hover:animate-bounce" />
              </a>
            </div>
          </div>
        </div>
        {/* Animated Scroll Indicator - always below content */}
        <div className="mt-4 sm:mt-8 flex flex-col items-center text-white animate-bounce">
          <span className="text-xs sm:text-sm mb-1 sm:mb-2 opacity-80">Scroll to explore</span>
          <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8" />
        </div>
      </div>

      {/* Custom CSS */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-30px) rotate(120deg); }
          66% { transform: translateY(-20px) rotate(240deg); }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-float {
          animation: float linear infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animation-delay-300 {
          animation-delay: 0.3s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        .animation-delay-900 {
          animation-delay: 0.9s;
        }

        .border-3 {
          border-width: 3px;
        }
      `}</style>
    </header>
  );
};

export default Hero; 